import { ReleaseNoteByod } from '@src/main/modules/parameters/domain/release-notes-byod/release-note-byod.interface';
import { ReleaseNoteByodRepository } from '@src/main/modules/parameters/domain/release-notes-byod/release-note-byod.repository';
import {
  CreateReleaseNoteByodRequest,
  PatchReleaseNoteByodRequest,
} from '@src/main/modules/parameters/infrastructure/dto/createUpdateReleaseNoteByodRequest';

import {
  ReleaseNoteByodEntity,
  toReleaseNoteByod,
} from '@src/main/modules/parameters/infrastructure/entity/release-note-byod.entity';
import { api, TokenProvider } from '@src/main/shared/infrastructure/entity/axios';

const releaseNotesByodPath = '/release_note_byod';

export const createReleaseNoteByodHttpRepository = (
  tokenProvider: TokenProvider,
): ReleaseNoteByodRepository => new ReleaseNoteByodHttpRepository(tokenProvider);

class ReleaseNoteByodHttpRepository implements ReleaseNoteByodRepository {
  private readonly tokenProvider: TokenProvider;

  constructor(tokenProvider: TokenProvider) {
    this.tokenProvider = tokenProvider;
  }

  private async getAuthToken(): Promise<string> {
    const token = await this.tokenProvider.getToken();
    if (!token) throw new Error('Authentication token is missing');
    return token;
  }

  async findAll(): Promise<ReleaseNoteByod[]> {
    const token = await this.getAuthToken();
    const response = await api.get<ReleaseNoteByodEntity[]>(`${releaseNotesByodPath}/all`, token);
    return response.map(toReleaseNoteByod);
  }

  async findById(id: string): Promise<ReleaseNoteByod> {
    const token = await this.getAuthToken();
    const response = await api.get<ReleaseNoteByodEntity>(`${releaseNotesByodPath}/${id}`, token);
    return toReleaseNoteByod(response);
  }

  async create(releaseNoteByod: CreateReleaseNoteByodRequest): Promise<ReleaseNoteByod> {
    const token = await this.getAuthToken();
    const response = await api.post<ReleaseNoteByodEntity>(
      releaseNotesByodPath,
      token,
      releaseNoteByod,
    );
    return toReleaseNoteByod(response);
  }

  async update(id: string, releaseNoteByod: PatchReleaseNoteByodRequest): Promise<ReleaseNoteByod> {
    const token = await this.getAuthToken();
    const response = await api.patch<ReleaseNoteByodEntity>(
      `${releaseNotesByodPath}/${id}`,
      token,
      releaseNoteByod,
    );
    return toReleaseNoteByod(response);
  }

  async delete(id: string): Promise<void> {
    const token = await this.getAuthToken();
    await api.delete(`${releaseNotesByodPath}/${id}`, token);
  }
}
